import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  ICliente,
  IDispositivoAlarma,
  IEventoGenerico,
  IFilter,
  ILayerMapaPersonalizado,
  IPopulate,
  IQueryParam,
  IUbicacion,
  IUbicacionBase,
  IReporteBase,
  IReporteGenerico,
} from 'modelos/src';
import { Subscription } from 'rxjs';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { TipoMapaBase } from '../../../standalone/selector-tipo-mapa/selector-tipo-mapa.component';
import { LoginService } from '../../../login/login.service';
import { MapaFacilV2Service } from '../../../../auxiliares/mapbox/mapa-facil-v2.service';
import { PolygonConfig } from '../../../../auxiliares/mapbox/entity-managers/polygons-layer.manager';
import mapboxgl from 'mapbox-gl';

interface IDispositivoAlarmaExtendido extends IDispositivoAlarma {
  eventosAbiertos?: IEventoGenerico[];
  eventosTecnicosAbiertos?: IEventoGenerico[];
}
@Component({
  selector: 'app-mapa-alarmas',
  templateUrl: './mapa-alarmas.component.html',
  styleUrl: './mapa-alarmas.component.scss',
  standalone: false,
})
export class MapaAlarmasComponent implements OnInit, OnDestroy {
  private lista2Service = inject(Lista2Service);
  private mapaFacilV2Service = inject(MapaFacilV2Service);

  public loading = false;

  //Alarmas
  public alarma?: IDispositivoAlarmaExtendido;
  public alarmas?: IDispositivoAlarmaExtendido[] = [];
  public alarmasSeleccionadas?: IDispositivoAlarmaExtendido[] = []; //Alarmas que cumplen con los filtros aplicados
  public alarmas$?: Subscription;

  public reportesVehiculosSeguridadPrivada?: IReporteBase<'Tracker 4G'>[] = [];
  public reportesVehiculosSeguridadPrivada$?: Subscription;

  public mostrarClusters = false;
  public mostrarVehiculos = true; // Por defecto mostrar vehículos de seguridad privada

  // Zonas geográficas (ubicaciones categoría 'Alarmas'). Se usan para pintar
  // los polígonos y como filtro: la pertenencia de una alarma a una zona no
  // está persistida, la resuelve el flotante por punto-en-polígono contra el
  // domicilio (mismo esquema que las zonas de luminarias).
  public zonas?: IUbicacionBase<'Alarmas'>[] = [];
  public zonas$?: Subscription;
  public mostrarPoligonosZonas = true;
  public mostrarTextoZonas = true;

  // Entidades permitidas para re-aplicar datos al cambiar capa base. Las entidades pueden ser las que están presentes en MapaFacilV2Data
  public allowedTypes: string[] = ['alarmas', 'vehiculos'];

  // Mapa
  public map: mapboxgl.Map | null = null;

  //MAPAS A MOSTRAR
  public readonly TIPO_MAPA_BASE_KEY = 'tipoMapaBaseAlarmasComponent'; //Key para ver el mapa base que está en el local Storage
  public tipoMapaBase: TipoMapaBase =
    localStorage.getItem(this.TIPO_MAPA_BASE_KEY) || 'osm'; //Mapa base que está en el local Storage
  public mapasPersonalizados: ILayerMapaPersonalizado[] = []; //Mapas personalizados del cliente
  public clientePropio: ICliente = LoginService.getClientePropio();

  // Popup
  public coordinatesPopup!: [number, number];
  public showPopup = false;

  // Payload mínimo: son ~6.000 alarmas y el pedido se hacía de 6 MB por traer
  // populates que este mapa no usa (cliente, modelo, camaras, sim1, sim2 — el
  // popup hace su propio getById, ver necesitaFetchDatos en mapa-facil-v2).
  private populateAlarmas: IPopulate[] = [
    {
      path: 'domicilio',
      select: 'geojson direccion',
    },
  ];
  private queryAlarmas: IQueryParam = {
    // Campos que consumen el mapa y el flotante: pin (idDomicilio), etiqueta
    // (nombre), filtros (idCliente, idsCamaras) y estado de reporte
    // (frecReporte + comunicadores[].fechaUltimaComunicacion).
    select:
      'idDomicilio idCliente nombre idsCamaras frecReporte comunicadores.fechaUltimaComunicacion',
    populate: JSON.stringify(this.populateAlarmas),
    includeChildren: true,
    excludeTotalCount: true,
    unlimited: true,
  };

  private queryEventos: IQueryParam = {
    select: 'valores.titulo valores.color fechaCreacion estado idEntidad',
    includeChildren: true,
    excludeTotalCount: true,
    unlimited: true,
  };
  private queryEventosTecnicos: IQueryParam = {
    select: 'fechaCreacion estado idEntidad valores.titulo',
    includeChildren: true,
    excludeTotalCount: true,
    unlimited: true,
  };

  /// Alarmas
  public async listar(): Promise<void> {
    this.alarmas$?.unsubscribe();
    this.alarmas$ = this.lista2Service
      .alarmasMapa(
        this.queryAlarmas,
        this.queryEventos,
        this.queryEventosTecnicos,
        120,
      )
      .subscribe({
        next: (data) => {
          // El pedido pesa varios MB y tarda unos segundos: recién acá se apaga
          // el indicador de carga (antes se apagaba al suscribirse, así que el
          // mapa quedaba vacío y sin ninguna señal de que estaba trabajando).
          this.loading = false;

          const alarmas = (
            data[2].datos as IDispositivoAlarmaExtendido[]
          ).filter((a) => a.domicilio?.geojson != null);
          // Índice por idEntidad en vez de un filter() por alarma: con ~6.000
          // alarmas y ~1.000 eventos el anidado eran millones de comparaciones
          // en el hilo principal (varios segundos de freeze en cada refresco).
          const porEntidad = (lista: IEventoGenerico[]) => {
            const mapa = new Map<string, IEventoGenerico[]>();
            for (const e of lista) {
              if (!e.idEntidad) continue;
              const previos = mapa.get(e.idEntidad);
              if (previos) previos.push(e);
              else mapa.set(e.idEntidad, [e]);
            }
            return mapa;
          };
          const eventos = porEntidad(data[0].datos);
          const eventosTecnicos = porEntidad(data[1].datos);
          for (const alarma of alarmas) {
            alarma.eventosAbiertos = eventos.get(alarma._id) ?? [];
            alarma.eventosTecnicosAbiertos =
              eventosTecnicos.get(alarma._id) ?? [];
          }
          this.alarmas = alarmas;
          this.addMarkersAlarmas();
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  private async listarReportesVehiculoSeguridadPrivada(): Promise<void> {
    const filter: IFilter<IReporteGenerico> = {
      tipoEntidad: 'Vehiculo',
    };

    const populate: IPopulate = [
      {
        path: 'activo',
        select: 'identificacion categoria vehiculo funcion',
      },
    ];

    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      populate: JSON.stringify(populate),
    };

    this.reportesVehiculosSeguridadPrivada$?.unsubscribe();
    this.reportesVehiculosSeguridadPrivada$ = this.lista2Service
      .ultimosReportesGenericos<'Tracker 4G'>(query, 15)
      .subscribe((data) => {
        const reportesFiltrados = data.datos;
        this.reportesVehiculosSeguridadPrivada = reportesFiltrados.filter(
          (r) =>
            r.activo?.categoria === 'Vehículo' &&
            r.activo?.funcion === 'Seguridad Privada',
        );
        this.addMarkersVehiculos();
      });
  }

  /**
   *  Nueva implementación simplificada
   * - Sin conversión a MarkerMapa[]
   * - Pasamos las alarmas directamente
   * - El AlarmasLayerManager se encarga de todo
   */
  private addMarkersAlarmas() {
    if (!this.mapaFacilV2Service.isReady()) return;

    // API simplificada: pasamos las alarmas directamente
    this.mapaFacilV2Service.updateData({
      alarmas: this.alarmasSeleccionadas || [],
    });
  }

  /// Zonas geográficas (ubicaciones categoría 'Alarmas') para sus polígonos
  public async listarZonas(): Promise<void> {
    const filter: IFilter<IUbicacion> = {
      categoria: 'Alarmas',
      'geojson.coordinates': { $exists: true, $ne: null },
    } as IFilter<IUbicacion>;
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      select: 'identificacion color geojson idCliente categoria',
      sort: 'identificacion',
      page: 0,
      includeChildren: true,
    };
    this.zonas$?.unsubscribe();
    this.zonas$ = this.lista2Service
      .ubicacions<'Alarmas'>(query, 30)
      .subscribe((data) => {
        this.zonas = data.datos;
        this.actualizarPoligonos();
      });
  }

  /// Pinta los polígonos de las zonas según el toggle
  private actualizarPoligonos(): void {
    if (!this.mapaFacilV2Service.isReady()) return;

    const polygons: PolygonConfig[] = [];

    if (this.mostrarPoligonosZonas) {
      for (const zona of this.zonas ?? []) {
        const geojson = zona.geojson;
        if (!geojson) continue;
        const color = zona.color || '#ffcc33';
        const label = this.mostrarTextoZonas ? zona.identificacion : undefined;
        if (geojson.type === 'Polygon') {
          polygons.push({
            points: geojson.coordinates[0] as [number, number][],
            color,
            fillColor: color,
            opacity: 0.3,
            label,
          });
        } else if (geojson.type === 'MultiPolygon') {
          for (const poly of geojson.coordinates) {
            polygons.push({
              points: poly[0] as [number, number][],
              color,
              fillColor: color,
              opacity: 0.3,
              label,
            });
          }
        }
      }
    }

    this.mapaFacilV2Service.updateData({ polygons });
  }

  public onPoligonoZonasToggle(mostrar: boolean) {
    this.mostrarPoligonosZonas = mostrar;
    this.actualizarPoligonos();
  }

  public onTextoZonasToggle(mostrar: boolean) {
    this.mostrarTextoZonas = mostrar;
    this.actualizarPoligonos();
  }

  /**
   * Nueva implementación simplificada para vehículos
   * - Sin conversión a MarkerMapa[]
   * - Pasamos los reportes directamente
   * - El VehiculosLayerManager se encarga de todo
   */
  private addMarkersVehiculos() {
    if (!this.mapaFacilV2Service.isReady()) return;

    // API simplificada: pasamos los vehículos directamente
    const vehiculos = this.mostrarVehiculos
      ? this.reportesVehiculosSeguridadPrivada || []
      : [];

    this.mapaFacilV2Service.updateData({
      vehiculos: vehiculos,
    });
  }

  /**
   * Toggle de clustering usando la nueva API
   */
  public onClusterToggle(clusterizar: boolean) {
    this.mostrarClusters = clusterizar;
    this.mapaFacilV2Service.setClusteringEnabled(
      'alarmas',
      this.mostrarClusters,
    );
  }

  /**
   * Toggle de visibilidad de vehículos
   */
  public onSeguridadPrivadaToggle(mostrar: boolean) {
    this.mostrarVehiculos = mostrar;
    this.addMarkersVehiculos();
  }

  public onAlarmaChange(l: IDispositivoAlarmaExtendido[]) {
    this.alarmasSeleccionadas = l;
    this.addMarkersAlarmas();
  }

  /**
   * Callback cuando el mapa está listo
   */
  public onMapReady(map: mapboxgl.Map): void {
    this.map = map;

    // Actualizar datos cuando el mapa esté listo
    this.addMarkersAlarmas();
    this.addMarkersVehiculos();
    this.actualizarPoligonos();
  }

  ///
  async ngOnInit() {
    this.loading = true;

    if (this.clientePropio?.mapLayers) {
      this.mapasPersonalizados = this.clientePropio.mapLayers;
    }

    // Cargar datos (el mapa se actualizará cuando esté listo).
    // Son suscripciones: `loading` se apaga en el primer emit de listar().
    await this.listar();
    await this.listarReportesVehiculoSeguridadPrivada();
    await this.listarZonas();
  }

  ngOnDestroy() {
    this.alarmas$?.unsubscribe();
    this.reportesVehiculosSeguridadPrivada$?.unsubscribe();
    this.zonas$?.unsubscribe();

    // IMPORTANTE: Limpiar datos del servicio para evitar que persistan al cambiar de mapa
    // El servicio es singleton, por lo que mantiene datos entre componentes
    this.mapaFacilV2Service.clearAllData();
  }
}
